import React from 'react';
import { AlertTriangle, Layers, MoreHorizontal, RefreshCcw, Repeat } from 'lucide-react';
import { NODE_TYPES } from './nodeTypes';
import { EXECUTION_STATUS_STYLES } from './executionStatus';
import { buildSimpleLayout } from './simpleLayout';
import AddStepButton from './AddStepButton';

const ROUTE_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#0ea5e9', '#8b5cf6'];

/**
 * Simple Mode renderer.
 *
 * Reads the same data the canvas reads and lays the workflow out as a
 * vertical card list with branches as side-by-side columns. Editing
 * happens via the existing Config panel; adding via inline + buttons
 * (insertNodeBetween / appendAfter on useWorkflowState).
 */
export default function SimpleWorkflowView({
  state,
  selectedNodeId,
  highlightedNodeId,
  activeRunNodeStatuses,
  onSelectNode,
  onSwitchToBuilder,
}) {
  const { nodes, edges, nodeConfigs, insertNodeBetween, appendAfter } = state;
  const { roots, orphanIds } = buildSimpleLayout({ nodes, edges, nodeConfigs });

  const isEmpty = nodes.length === 0;

  const handleInsert = (prevId, nextId, sourceHandle, typeKey) => {
    const newId = insertNodeBetween({ prevId, nextId, sourceHandle, typeKey });
    if (newId) onSelectNode(newId);
  };
  const handleAppend = (prevId, sourceHandle, typeKey) => {
    const newId = appendAfter({ prevId, sourceHandle, typeKey });
    if (newId) onSelectNode(newId);
  };

  return (
    <div className="flex-1 overflow-y-auto bg-[#fafafa]">
      <div className="max-w-2xl mx-auto px-4 py-10">
        {isEmpty && (
          <div className="text-center py-20">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-white border border-gray-200 flex items-center justify-center mb-4">
              <Layers className="w-5 h-5 text-gray-400" strokeWidth={1.6} />
            </div>
            <p className="text-[14px] text-gray-700 mb-1">No steps yet</p>
            <p className="text-[12.5px] text-gray-500 mb-4">
              Switch to Builder mode to add a trigger and your first steps.
            </p>
            <button
              onClick={onSwitchToBuilder}
              className="text-[12.5px] font-medium text-gray-700 hover:text-gray-900 underline underline-offset-2 decoration-gray-300 hover:decoration-gray-500"
            >
              Open Builder mode →
            </button>
          </div>
        )}

        {roots.map((root, idx) => (
          <Chain
            key={root.trigger.id}
            chain={root.chain}
            triggerLabel={roots.length > 1 ? `Trigger ${idx + 1}` : 'Trigger'}
            selectedNodeId={selectedNodeId}
            highlightedNodeId={highlightedNodeId}
            nodeConfigs={nodeConfigs}
            activeRunNodeStatuses={activeRunNodeStatuses}
            onSelectNode={onSelectNode}
            onInsert={handleInsert}
            onAppend={handleAppend}
          />
        ))}

        {orphanIds.length > 0 && (
          <OrphanSection
            orphanIds={orphanIds}
            nodes={nodes}
            nodeConfigs={nodeConfigs}
            selectedNodeId={selectedNodeId}
            onSelectNode={onSelectNode}
          />
        )}

        {!isEmpty && (
          <div className="mt-10 pt-6 border-t border-gray-100 text-center">
            <p className="text-[12px] text-gray-400 mb-2">
              Need to drag steps around or build complex shapes?
            </p>
            <button
              onClick={onSwitchToBuilder}
              className="text-[12.5px] font-medium text-gray-600 hover:text-gray-900"
            >
              Open Builder mode →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Render a chain of layout items at the top level. `sourceContext`
 * captures how `+` buttons should connect:
 *   - prevId: the id of the previous step in the chain
 *   - branchEntry: { sourceId, sourceHandle } when this is the first
 *     position inside a branch (so the "Add at top" button connects
 *     from the branch source's specific handle)
 *   - convergeId: when defined, "Add at end" of this branch chain is
 *     an INSERT between the last branch step and the converge node
 */
function Chain({
  chain,
  triggerLabel,
  selectedNodeId,
  highlightedNodeId,
  nodeConfigs,
  activeRunNodeStatuses,
  onSelectNode,
  onInsert,
  onAppend,
  branchEntry = null,
  convergeId = null,
  // Hide the "+ Add at end" button when the parent is responsible for
  // the post-chain add affordance (e.g. inside a loop body).
  suppressTrailingAdd = false,
}) {
  return (
    <div className="space-y-2.5">
      {chain.map((item, idx) => {
        const prevItem = chain[idx - 1] || null;

        const renderInsertButtonAbove =
          idx > 0 && prevItem && prevItem.kind === 'step' && item.kind === 'step';

        return (
          <React.Fragment key={`${item.kind}-${idx}`}>
            {renderInsertButtonAbove && (
              <AddStepButton
                onSelect={(typeKey) =>
                  onInsert(prevItem.node.id, item.node.id, null, typeKey)
                }
              />
            )}
            {idx > 0 && !renderInsertButtonAbove && <Connector />}

            {item.kind === 'step' && (
              <StepCard
                node={item.node}
                config={nodeConfigs[item.node.id]}
                selected={selectedNodeId === item.node.id}
                highlighted={highlightedNodeId === item.node.id}
                executionStatus={activeRunNodeStatuses[item.node.id] || null}
                onClick={() => onSelectNode(item.node.id)}
                topBadge={
                  idx === 0 && !branchEntry ? triggerLabel : null
                }
              />
            )}

            {item.kind === 'branch' && (
              <>
                <StepCard
                  node={item.source}
                  config={nodeConfigs[item.source.id]}
                  selected={selectedNodeId === item.source.id}
                  highlighted={highlightedNodeId === item.source.id}
                  executionStatus={activeRunNodeStatuses[item.source.id] || null}
                  onClick={() => onSelectNode(item.source.id)}
                />
                <BranchSplit
                  source={item.source}
                  branches={item.branches}
                  convergeId={item.convergeId}
                  selectedNodeId={selectedNodeId}
                  highlightedNodeId={highlightedNodeId}
                  nodeConfigs={nodeConfigs}
                  activeRunNodeStatuses={activeRunNodeStatuses}
                  onSelectNode={onSelectNode}
                  onInsert={onInsert}
                  onAppend={onAppend}
                />
              </>
            )}

            {item.kind === 'loop' && (
              <LoopBlock
                source={item.source}
                body={item.body}
                config={nodeConfigs[item.source.id]}
                selected={selectedNodeId === item.source.id}
                highlighted={highlightedNodeId === item.source.id}
                executionStatus={activeRunNodeStatuses[item.source.id] || null}
                selectedNodeId={selectedNodeId}
                highlightedNodeId={highlightedNodeId}
                nodeConfigs={nodeConfigs}
                activeRunNodeStatuses={activeRunNodeStatuses}
                onSelectNode={onSelectNode}
                onInsert={onInsert}
                onAppend={onAppend}
              />
            )}

            {item.kind === 'cycle-warning' && <CycleWarning node={item.node} />}
          </React.Fragment>
        );
      })}

      {/* Trailing add — append OR insert-before-converge depending on context. */}
      {!suppressTrailingAdd && (
        <TrailingAdd
          chain={chain}
          branchEntry={branchEntry}
          convergeId={convergeId}
          onInsert={onInsert}
          onAppend={onAppend}
        />
      )}
    </div>
  );
}

function TrailingAdd({ chain, branchEntry, convergeId, onInsert, onAppend }) {
  const lastStepItem = [...chain].reverse().find((c) => c.kind === 'step');
  // If chain is empty (no children yet), the add attaches directly to the
  // branch entry handle.
  if (chain.length === 0) {
    if (!branchEntry) return null;
    return (
      <AddStepButton
        label="Add a step"
        onSelect={(typeKey) => {
          if (convergeId) {
            // No existing branch step; insert directly between source-handle
            // and converge would be odd. Append from the branch entry instead;
            // user can rewire later.
            onAppend(branchEntry.sourceId, branchEntry.sourceHandle, typeKey);
          } else {
            onAppend(branchEntry.sourceId, branchEntry.sourceHandle, typeKey);
          }
        }}
      />
    );
  }
  // If the chain ends with a non-step (cycle-warning / branch / loop), don't
  // offer to add. PR 2c+ can refine.
  const last = chain[chain.length - 1];
  if (!last || last.kind !== 'step' || !lastStepItem) return null;

  if (convergeId) {
    // We're at the end of a branch chain — insert between the last branch
    // step and the converge node.
    return (
      <AddStepButton
        label="Add a step"
        onSelect={(typeKey) =>
          onInsert(lastStepItem.node.id, convergeId, null, typeKey)
        }
      />
    );
  }
  // Top-level chain end: append after the last step.
  return (
    <AddStepButton
      label="Add a step"
      onSelect={(typeKey) => onAppend(lastStepItem.node.id, null, typeKey)}
    />
  );
}

function StepCard({
  node,
  config,
  selected,
  highlighted,
  executionStatus,
  onClick,
  topBadge,
}) {
  const typeDef = NODE_TYPES[node.data.nodeType];
  if (!typeDef) return null;
  const Icon = typeDef.icon;
  const label = node.data.label || typeDef.label;
  const subtitle = configPreview(node.data.nodeType, config);
  const statusStyle = executionStatus ? EXECUTION_STATUS_STYLES[executionStatus] : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full text-left bg-white rounded-xl border transition-all px-4 py-3.5 ${
        highlighted
          ? 'border-amber-300 ring-2 ring-amber-200 shadow-sm'
          : selected
          ? 'border-gray-400 ring-1 ring-gray-300 shadow-sm'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      {statusStyle && statusStyle.badge}
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: `${typeDef.color}14` }}
        >
          <Icon className="w-4 h-4" style={{ color: typeDef.color }} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          {topBadge && (
            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
              {topBadge}
            </span>
          )}
          <div className="text-[14px] font-medium text-gray-900 truncate">
            {label}
          </div>
          {subtitle && (
            <div className="text-[12.5px] text-gray-500 mt-0.5 truncate">
              {subtitle}
            </div>
          )}
        </div>
        <span className="text-gray-300 flex-shrink-0 mt-1">
          <MoreHorizontal className="w-4 h-4" />
        </span>
      </div>
    </button>
  );
}

function BranchSplit({
  source,
  branches,
  convergeId,
  selectedNodeId,
  highlightedNodeId,
  nodeConfigs,
  activeRunNodeStatuses,
  onSelectNode,
  onInsert,
  onAppend,
}) {
  return (
    <div className="relative">
      <div className="flex gap-3 items-stretch">
        {branches.map((branch, bidx) => {
          const color = ROUTE_COLORS[bidx % ROUTE_COLORS.length];
          const branchEntry = {
            sourceId: source.id,
            sourceHandle: branch.sourceHandle,
          };
          return (
            <div
              key={`${branch.sourceHandle || 'next'}-${bidx}`}
              className="flex-1 min-w-[220px] flex flex-col"
            >
              <div className="mb-2 flex items-center gap-1.5">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span className="text-[11px] uppercase tracking-wide font-medium text-gray-500 truncate">
                  {branch.label}
                </span>
              </div>
              <div className="flex-1">
                <Chain
                  chain={branch.children}
                  triggerLabel={null}
                  selectedNodeId={selectedNodeId}
                  highlightedNodeId={highlightedNodeId}
                  nodeConfigs={nodeConfigs}
                  activeRunNodeStatuses={activeRunNodeStatuses}
                  onSelectNode={onSelectNode}
                  onInsert={onInsert}
                  onAppend={onAppend}
                  branchEntry={branchEntry}
                  convergeId={convergeId || null}
                />
              </div>
            </div>
          );
        })}
      </div>
      {convergeId && <Connector />}
    </div>
  );
}

function LoopBlock({
  source,
  body,
  config,
  selected,
  highlighted,
  executionStatus,
  selectedNodeId,
  highlightedNodeId,
  nodeConfigs,
  activeRunNodeStatuses,
  onSelectNode,
  onInsert,
  onAppend,
}) {
  const typeDef = NODE_TYPES[source.data.nodeType];
  if (!typeDef) return null;
  const Icon = typeDef.icon;
  const label = source.data.label || typeDef.label;
  const subtitle = configPreview(source.data.nodeType, config);
  const statusStyle = executionStatus ? EXECUTION_STATUS_STYLES[executionStatus] : null;

  return (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={() => onSelectNode(source.id)}
        className={`relative w-full text-left bg-white rounded-xl border transition-all px-4 py-3.5 ${
          highlighted
            ? 'border-amber-300 ring-2 ring-amber-200 shadow-sm'
            : selected
            ? 'border-gray-400 ring-1 ring-gray-300 shadow-sm'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }`}
      >
        {statusStyle && statusStyle.badge}
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ backgroundColor: `${typeDef.color}14` }}
          >
            <Icon className="w-4 h-4" style={{ color: typeDef.color }} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1">
              <Repeat className="w-3 h-3" /> Loop
            </span>
            <div className="text-[14px] font-medium text-gray-900 truncate">
              {label}
            </div>
            {subtitle && (
              <div className="text-[12.5px] text-gray-500 mt-0.5 truncate">
                {subtitle}
              </div>
            )}
          </div>
        </div>
      </button>

      <div className="ml-4 pl-4 border-l-2 border-brand-400/70">
        <p className="text-[11px] uppercase tracking-wide font-medium text-gray-400 mb-2">
          For each item
        </p>
        {body.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-white/40 px-3 py-3 text-center text-[12px] text-gray-400">
            No steps in the loop
          </div>
        ) : (
          <Chain
            chain={body}
            triggerLabel={null}
            selectedNodeId={selectedNodeId}
            highlightedNodeId={highlightedNodeId}
            nodeConfigs={nodeConfigs}
            activeRunNodeStatuses={activeRunNodeStatuses}
            onSelectNode={onSelectNode}
            onInsert={onInsert}
            onAppend={onAppend}
            branchEntry={{ sourceId: source.id, sourceHandle: 'body' }}
            suppressTrailingAdd
          />
        )}
      </div>
      <p className="text-[11px] uppercase tracking-wide font-medium text-gray-400 text-center pt-1">
        End loop
      </p>
    </div>
  );
}

function Connector({ compact = false }) {
  return (
    <div
      className={`mx-auto w-0.5 ${compact ? 'h-3' : 'h-4'} bg-brand-400/70`}
      aria-hidden="true"
    />
  );
}

function CycleWarning({ node }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-start gap-3">
      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
      <div className="text-[12.5px] text-amber-900">
        <strong className="font-medium">Loops back</strong> to{' '}
        <span className="font-mono text-[12px] text-amber-800">
          {node.data?.label || node.id}
        </span>
        . Switch to Builder mode to inspect the cycle.
      </div>
    </div>
  );
}

function OrphanSection({
  orphanIds,
  nodes,
  nodeConfigs,
  selectedNodeId,
  onSelectNode,
}) {
  const orphans = orphanIds
    .map((id) => nodes.find((n) => n.id === id))
    .filter(Boolean);
  if (orphans.length === 0) return null;
  return (
    <div className="mt-8 pt-6 border-t border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCcw className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-[11.5px] uppercase tracking-wide text-gray-500 font-medium">
          Disconnected steps ({orphans.length})
        </span>
      </div>
      <p className="text-[12.5px] text-gray-500 mb-3">
        These steps aren't connected to a trigger and won't run.
      </p>
      <div className="space-y-2.5">
        {orphans.map((node) => (
          <StepCard
            key={node.id}
            node={node}
            config={nodeConfigs[node.id]}
            selected={selectedNodeId === node.id}
            highlighted={false}
            executionStatus={null}
            onClick={() => onSelectNode(node.id)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Best-effort one-line preview of a node's config. Avoids surfacing
 * keys whose names suggest credentials.
 */
function configPreview(nodeType, config) {
  if (!config) return null;
  const trunc = (s, n = 60) => {
    const str = String(s ?? '');
    return str.length > n ? str.slice(0, n - 1) + '…' : str;
  };

  switch (nodeType) {
    case 'sendEmail':
      if (config.subject) return `Subject: ${trunc(config.subject)}`;
      if (config.to) return `To: ${trunc(config.to)}`;
      return null;
    case 'createRecord':
    case 'updateRecord':
    case 'getRecord':
    case 'deleteRecord':
    case 'searchRecords':
      if (config.objectDefinitionId) return `Object: ${trunc(config.objectDefinitionId)}`;
      return null;
    case 'httpRequest':
      if (config.url) return `${(config.method || 'GET').toUpperCase()} ${trunc(config.url)}`;
      return null;
    case 'linkedinPersonFinder': {
      const name = [config.firstName, config.lastName].filter(Boolean).join(' ');
      if (name) return `Find ${trunc(name)}`;
      return null;
    }
    case 'linkedinCompanyEmployees': {
      if (config.companyUrls) return `Company: ${trunc(config.companyUrls)}`;
      return null;
    }
    case 'googleSearchResults': {
      if (config.queries) return `Query: ${trunc(config.queries)}`;
      return null;
    }
    case 'linkedinJobsSearch': {
      if (config.searchTerms) return `Search: ${trunc(config.searchTerms)}`;
      return null;
    }
    case 'googleMapsReviews': {
      if (config.placeUrls) return `Place: ${trunc(config.placeUrls)}`;
      return null;
    }
    case 'facebookAdsLibrarySearch': {
      if (config.searchTerms) return `Keywords: ${trunc(config.searchTerms)}`;
      if (config.pageIds) return `Page IDs: ${trunc(config.pageIds)}`;
      if (config.adLibraryUrls) return `URL: ${trunc(config.adLibraryUrls)}`;
      return null;
    }
    case 'instagramProfileSnapshot': {
      if (config.profileUrlsOrUsernames) return `Profiles: ${trunc(config.profileUrlsOrUsernames)}`;
      return null;
    }
    case 'llm':
      if (config.prompt) return trunc(config.prompt, 80);
      return null;
    case 'conditional': {
      const c = config.condition || config;
      const left = c.left;
      const op = c.operator;
      const right = c.right;
      if (left || op || right) {
        return `If ${trunc(left, 24)} ${op || '?'} ${trunc(right, 24)}`;
      }
      return null;
    }
    case 'router':
      if (config.rules?.length) return `${config.rules.length} routes`;
      return null;
    case 'iterate':
      if (config.source) return `Over ${trunc(config.source)}`;
      return null;
    case 'delay':
      if (config.seconds != null) return `Wait ${config.seconds}s`;
      if (config.until) return `Until ${trunc(config.until)}`;
      return null;
    case 'cron':
      if (config.schedule) return `Schedule: ${config.schedule}`;
      return null;
    case 'formSubmission':
      if (config.formId) return `Form: ${trunc(config.formId)}`;
      return null;
    default:
      return null;
  }
}
